import type { GraphItem, SyncSelection } from "../../domain/entities/sharepoint"

export const ACCEPTED_EXTENSIONS = ["pdf", "docx", "xlsx", "csv", "txt", "md"]

export const selKey = (driveId: string, itemId: string): string => `${driveId}:${itemId}`

export const isSelectable = (item: GraphItem): boolean => {
  if (item.isFolder) return false
  const dot = item.name.lastIndexOf(".")
  if (dot < 0) return false
  return ACCEPTED_EXTENSIONS.includes(item.name.slice(dot + 1).toLowerCase())
}

const toSelection = (item: GraphItem, path: string): SyncSelection => ({
  driveId: item.driveId,
  itemId: item.id,
  name: item.name,
  size: item.size,
  isFolder: item.isFolder,
  path,
  lastModified: item.lastModified ?? null,
})

export const toggleSelection = (
  selected: Map<string, SyncSelection>,
  item: GraphItem,
  path: string,
): Map<string, SyncSelection> => {
  const next = new Map(selected)
  const key = selKey(item.driveId, item.id)
  if (next.has(key)) next.delete(key)
  else next.set(key, toSelection(item, path))
  return next
}

export const selectAllVisible = (
  selected: Map<string, SyncSelection>,
  items: GraphItem[],
  pathOf: (item: GraphItem) => string,
): Map<string, SyncSelection> => {
  const next = new Map(selected)
  for (const it of items) {
    if (isSelectable(it)) next.set(selKey(it.driveId, it.id), toSelection(it, pathOf(it)))
  }
  return next
}

export const unselectVisible = (
  selected: Map<string, SyncSelection>,
  items: GraphItem[],
): Map<string, SyncSelection> => {
  const next = new Map(selected)
  for (const it of items) next.delete(selKey(it.driveId, it.id))
  return next
}

export const buildDisplayPath = (crumbLabels: string[], name: string): string =>
  [...crumbLabels, name].filter(Boolean).join("/")

export const truncatePath = (fullPath: string): string => {
  const parts = fullPath.split("/").filter(Boolean)
  if (parts.length <= 2) return parts.join("/")
  return `.../${parts.slice(-2).join("/")}`
}

// Split an abbreviated path into a shrinkable prefix (".../parent/") and the
// file name. The name is rendered non-shrinking so it is always fully visible;
// only the prefix ellipsizes when the row is too narrow.
export const displayPathParts = (
  fullPath: string,
): { prefix: string; name: string } => {
  const abbreviated = truncatePath(fullPath)
  const idx = abbreviated.lastIndexOf("/")
  if (idx < 0) return { prefix: "", name: abbreviated }
  return { prefix: abbreviated.slice(0, idx + 1), name: abbreviated.slice(idx + 1) }
}
